import { type MessageItem } from "@koala/sdk";
import Link from "next/link";
import { Component } from "react";
import { type ConnectedProps, connect } from "react-redux";
import {
  StyledButtonContainer,
  StyledCloseMessageIcon,
  StyledExpandedMessageBody,
  StyledExpandedMessageContainer,
  StyledExpandedMessageHeader,
  StyledMessageCtas,
  StyledMessageIcon,
  StyledMessageIcons,
} from "./styles";
import LoyaltyAccessor from "@/components/account/loyaltyAccessor";
import { XSvg } from "@/components/uielements/X";
import {
  LOYALTY_ACCESSOR_TYPES,
  LOYALTY_FEATURES,
  LOYALTY_MESSAGE_TYPES,
  MESSAGE_CTA_TYPES,
  MESSAGE_HERO_TYPES,
} from "@/constants/loyalty";
import meActions from "@/redux/me/actions";
import { dateFormatter, routePunchhDeepLinks } from "@/utils/loyalty";

interface Props extends ReduxProps {
  closeMessage: () => void;
  message: MessageItem;
}

class ExpandedMessage extends Component<Props> {
  render() {
    const { closeMessage, message } = this.props;

    // Split message body into lines
    const parsedMessageBody = message.body.split("\n");

    return (
      <StyledExpandedMessageContainer
        backgroundImage={message?.background_content?.url}
      >
        <div>
          <StyledExpandedMessageHeader>
            <StyledMessageIcons>
              <StyledCloseMessageIcon
                onClick={() => closeMessage()}
                aria-label="Click to close this message"
              >
                <XSvg />
              </StyledCloseMessageIcon>

              {/* Business messages can't be deleted */}
              {message.type !== LOYALTY_MESSAGE_TYPES.BUSINESS && (
                <LoyaltyAccessor
                  checkType={LOYALTY_ACCESSOR_TYPES.FEATURE}
                  checkName={LOYALTY_FEATURES.DESTROY_LOYALTY_MESSAGE}
                  component={
                    <StyledMessageIcon
                      onClick={() => this.props.deleteMessage(message.id)}
                      aria-label="Click to delete this message"
                    >
                      <img
                        src="/static/img/icons/delete.png"
                        alt="Delete Icon"
                        role="presentation"
                      />
                    </StyledMessageIcon>
                  }
                />
              )}
            </StyledMessageIcons>

            <h1>{message.title}</h1>
          </StyledExpandedMessageHeader>
          {message.launch_time && (
            <p style={{ margin: 0 }}>{dateFormatter(message.launch_time)}</p>
          )}
          <StyledExpandedMessageBody>
            {/* Hero image or video */}
            {message.hero_content?.url &&
              (message.hero_content.type === MESSAGE_HERO_TYPES.VIDEO ? (
                <video src={message.hero_content?.url} controls />
              ) : (
                <img
                  src={message.hero_content?.url}
                  alt={message.hero_content?.alt_text}
                />
              ))}

            {/* Message content */}
            <h4>{message.subtitle}</h4>

            {/* Create new p tag for each message line */}
            <>
              {parsedMessageBody.map((line, index) => (
                <p key={`line-${index}`}>{line.trim()}</p>
              ))}
            </>

            {/* Message CTAs */}
            <StyledButtonContainer>
              {message.message_ctas
                .filter((cta) => cta.type !== MESSAGE_CTA_TYPES.DRILL_DOWN)
                .map((cta, index) => {
                  // TODO: improve this type and remove string type to narrow it to proper union type
                  // Second CTA will be knockout theme
                  const buttonTheme = index === 1 ? "knockout" : "";

                  if (cta.type === MESSAGE_CTA_TYPES.DEEP_LINK) {
                    return (
                      <Link
                        href={routePunchhDeepLinks(cta)}
                        key={index}
                        passHref={true}
                      >
                        <StyledMessageCtas buttonTheme={buttonTheme} as="a">
                          <span>{cta.label}</span>
                        </StyledMessageCtas>
                      </Link>
                    );
                  }

                  return (
                    <StyledMessageCtas
                      buttonTheme={buttonTheme}
                      as="a"
                      href={cta.url}
                      target="_blank"
                      key={index}
                    >
                      <span>{cta.label}</span>
                    </StyledMessageCtas>
                  );
                })}
            </StyledButtonContainer>
          </StyledExpandedMessageBody>
        </div>
      </StyledExpandedMessageContainer>
    );
  }
}

const mapDispatchToProps = {
  deleteMessage: meActions.deleteMessage,
};

const connector = connect(null, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(ExpandedMessage);
